import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AuthContext } from "../context/AuthContext";
import { useThemeColors } from "../context/ThemeContext";
import { HEADING_FONT } from "../theme/fonts";
import api from "../api";
import { autoRegisterPush } from "../utils/push";
import logo from "../assets/image/logo.png.webp";

const FEATURES = [
  {
    icon: <LanguageIcon sx={{ fontSize: 22 }} />,
    title: "Domain Portfolio",
    desc: "Track every domain, registrar and expiry in one place",
  },
  {
    icon: <MonitorHeartOutlinedIcon sx={{ fontSize: 22 }} />,
    title: "Live Health Monitoring",
    desc: "Real-time uptime, SSL & DNS checks every 30 minutes",
  },
  {
    icon: <DnsOutlinedIcon sx={{ fontSize: 22 }} />,
    title: "Server Inventory",
    desc: "Hosting credentials and renewal timelines at a glance",
  },
  {
    icon: <HistoryEduOutlinedIcon sx={{ fontSize: 22 }} />,
    title: "Client Activity Log",
    desc: "Renewal reminders and conversation history per domain",
  },
];

function Login() {
  const navigate = useNavigate();
  const { signIn } = useContext(AuthContext);
  const colors = useThemeColors();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const raw = email.trim();
    const username = raw.includes("@") ? raw.split("@")[0] : raw;

    try {
      const result = await api.post("/auth/login", { username, password });

      // Make the JWT available immediately so the push-subscribe call below is
      // authenticated (the AuthContext effect also persists it, but async).
      if (result?.token) localStorage.setItem("authToken", result.token);

      signIn(result);

      // Go straight to the dashboard — don't make the user wait on Firebase.
      navigate("/dashboard");

      // Register this device's FCM token in the BACKGROUND. Previously this was
      // awaited before navigate(), so login hung on the notification-permission
      // popup + Firebase SDK + FCM round-trip (often several seconds). It's
      // best-effort and optional, so fire-and-forget it instead.
      autoRegisterPush(true).catch(() => {
        // ignore — push is optional and must never block login
      });
    } catch (err) {
      if (err.code === "NETWORK_ERROR") {
        setError(
          "Cannot reach the server. Please make sure the backend is running.",
        );
      } else if (err.status === 401) {
        setError(
          "Invalid credentials. Please check your username and password.",
        );
      } else {
        setError(err.message || "Login failed. Please try again.");
      }
      setLoading(false);
    }
  }

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      fontSize: 14,
      transition: "all 0.15s ease",
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": {
        borderColor: "#818cf8",
        borderWidth: "1.5px",
      },
    },
    "& input": {
      color: colors.text,
      "&::placeholder": { color: colors.textFaint, opacity: 1 },
      // Chrome/Edge autofill paints the <input> a light-blue, leaving the icon
      // adornment areas white — making the box look two-toned. Force the
      // autofill background back to the field's own colour so the whole box
      // (icons + text) stays uniform.
      "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
        WebkitBoxShadow: `0 0 0 1000px ${colors.cardBg} inset`,
        WebkitTextFillColor: colors.text,
        caretColor: colors.text,
        transition: "background-color 9999s ease-in-out 0s",
      },
    },
    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
      color: colors.textMuted,
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        background: colors.bg,
        fontFamily:
          "'Work Sans', 'Work Sans Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* LEFT — gradient hero (mirrors the dashboard's hero banner) */}
      <Box
        sx={{
          display: "flex",
          width: { xs: "100%", lg: "50%" },
          alignItems: "flex-start",
          justifyContent: "center",
          p: { xs: 3, sm: 5, lg: 6 },
          pt: { xs: 6, sm: 8, lg: 10 },
          // minHeight: "100vh",
        }}
      >
        <Box
          component="form"
          onSubmit={handleLogin}
          sx={{
            width: "100%",
            // Constrain + centre the form on mobile/tablet; on desktop cap it
            // so inputs don't stretch absurdly wide on large/ultrawide screens.
            maxWidth: { xs: 420, lg: 480 },
            mx: { xs: "auto", lg: 0 },
            position: "relative",
          }}
        >
         <Box sx={{display: "flex", justifyContent:"center", mt: -4 , mb: 1}}>
            <Box
              component="img"
              src={logo}
              alt="RABS"
              sx={{
                height: 90,
                objectFit: "contain",
                // mt: -10
              }}
            />
            </Box>
          {/* Headline */}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#818cf8",
              textTransform: "uppercase",
              letterSpacing: "3px",
              display: "flex",
              justifyContent: "center",
              mb: 1.25,
            }}
          >
            Sign in
          </Typography>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: { xs: 26, sm: 30 },
              fontWeight: 800,
              color: colors.text,
              letterSpacing: "-0.5px",
              lineHeight: 1.15,
              display: "flex",
              justifyContent: "center",
              mb: 1,
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: colors.textMuted,
              display: "flex",
              justifyContent: "center",
              mb: 4,
              lineHeight: 1.5,
            }}
          >
            Enter your credentials to access the workspace.
          </Typography>

          {error && (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                mb: 2.5,
                fontSize: 13,
                alignItems: "center",
                background: colors.darkMode
                  ? "rgba(239, 68, 68, 0.1)"
                  : "rgba(239, 68, 68, 0.05)",
                borderColor: "rgba(239, 68, 68, 0.35)",
                color: colors.darkMode ? "#fecaca" : "#b91c1c",
                "& .MuiAlert-icon": { color: "#ef4444" },
              }}
            >
              {error}
            </Alert>
          )}

          <Stack spacing={2.25}>
            <Box>
              <Typography
                component="label"
                htmlFor="login-username"
                sx={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: colors.text,
                  mb: 1,
                }}
              >
                Username or Email
              </Typography>
              <TextField
                id="login-username"
                type="text"
                placeholder="admin"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="username"
                required
                fullWidth
                size="medium"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={fieldSx}
              />
            </Box>

            <Box>
              <Typography
                component="label"
                htmlFor="login-password"
                sx={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: colors.text,
                  mb: 1,
                }}
              >
                Password
              </Typography>
              <TextField
                id="login-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="current-password"
                required
                fullWidth
                size="medium"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                          tabIndex={-1}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          sx={{
                            background: "transparent",
                            mr: 0.25,
                            "& .MuiSvgIcon-root": {
                              fontSize: 18,
                              color: colors.textMuted,
                            },
                            "&:hover": {
                              background: "transparent",
                              "& .MuiSvgIcon-root": {
                                color: "#818cf8",
                              },
                            },
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOffOutlinedIcon />
                          ) : (
                            <VisibilityOutlinedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={fieldSx}
              />
              <Box sx={{ mt: 1 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  sx={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    p: 0,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#818cf8",
                    fontFamily: "inherit",
                    "&:hover": {
                      color: "#4f46e5",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Forgot password?
                </Box>
              </Box>
            </Box>

            <Button
              type="submit"
              disabled={loading}
              endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
              sx={{
                mt: 1.5,
                py: 1.6,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 14.5,
                fontWeight: 700,
                letterSpacing: "0.2px",
                color: "#fff",
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 8px 24px rgba(79, 70, 229, 0.35)",
                transition: "all 0.15s ease",
                "&:hover": {
                  boxShadow: "0 12px 32px rgba(79, 70, 229, 0.45)",
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "translateY(0)" },
                "&.Mui-disabled": {
                  color: "rgba(255,255,255,0.7)",
                  background:
                    "linear-gradient(135deg, rgba(79,70,229,0.55) 0%, rgba(124,58,237,0.55) 100%)",
                },
              }}
            >
              {loading ? (
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      animation: "spin 0.7s linear infinite",
                      "@keyframes spin": {
                        to: { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                  <span>Signing in…</span>
                </Stack>
              ) : (
                "Sign in to workspace"
              )}
            </Button>
          </Stack>

          {/* Footer note */}
          <Typography
            sx={{
              mt: 4,
              textAlign: "center",
              fontSize: 12,
              color: colors.textFaint,
            }}
          >
            Need access? Contact your administrator.
          </Typography>
        </Box>
      </Box>

      {/* RIGHT — sign-in form */}
      <Box
        sx={{
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
          color: "#fff",
          width: "50%",
          p: { lg: 6, xl: 8 },
          background:
            "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%)",
        }}
      >
        {/* Decorative blobs */}
        <Box
          sx={{
            position: "absolute",
            top: -120,
            right: -80,
            width: 360,
            height: 360,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.15)",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -140,
            left: -100,
            width: 380,
            height: 380,
            borderRadius: "50%",
            background: "rgba(168,85,247,0.45)",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        {/* Faint grid texture */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 80%)",
            pointerEvents: "none",
          }}
        />

        {/* Headline + features */}
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 540 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "3px",
              textTransform: "uppercase",
              opacity: 0.85,
              mb: 2,
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: { lg: 40, xl: 46 },
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-1px",
              mb: 2,
              background: "linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Your domains, servers and clients —
            <br />
            all in one workspace.
          </Typography>
          <Typography
            sx={{
              fontSize: 15,
              color: "rgba(255,255,255,0.78)",
              lineHeight: 1.55,
              mb: 4,
            }}
          >
            Sign in to manage every domain in your portfolio, monitor uptime
            &amp; SSL in real time, and keep client conversations organised.
          </Typography>

          <Stack spacing={2}>
            {FEATURES.map((f) => (
              <Stack
                key={f.title}
                direction="row"
                spacing={2}
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 1.5,
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.12) 100%)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    backdropFilter: "blur(8px)",
                  }}
                >
                  {f.icon}
                </Box>
                <Box sx={{ pt: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: 14.5,
                      fontWeight: 700,
                      color: "#fff",
                      mb: 0.25,
                    }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "rgba(255,255,255,0.7)",
                      lineHeight: 1.4,
                    }}
                  >
                    {f.desc}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          {/* Footer */}
          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              fontSize: 12,
              color: "rgba(255,255,255,0.5)",
              mt: 5,
            }}
          >
            © {new Date().getFullYear()} RABS Net Solutions Pvt. Ltd. · All
            rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* Forgot password dialog */}
      <Dialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${colors.divider}`,
            pb: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SupportAgentOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: 17,
                fontWeight: 700,
                color: colors.text,
              }}
            >
              Reset your password
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 0.5 }}>
          <Typography
            sx={{
              fontSize: 13.5,
              color: colors.textMuted,
              lineHeight: 1.55,
              mb: 2.5,
            }}
          >
            Password resets are handled by your workspace administrator. Please
            reach out using the contact below and they'll set a new password for
            your account.
          </Typography>
          <Stack
            spacing={1.25}
            sx={{
              p: 2,
              borderRadius: 2,
              background: colors.hover,
              border: `1px solid ${colors.cardBorder}`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  background: "rgba(79, 70, 229, 0.12)",
                  color: "#818cf8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: colors.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Email administrator
                </Typography>
                <Typography
                  component="a"
                  href="mailto:admin@rabsworld.in?subject=Password%20reset%20request"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.text,
                    textDecoration: "none",
                    "&:hover": { color: "#818cf8" },
                  }}
                >
                  admin@rabsworld.in
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ p: 2.5, borderTop: `1px solid ${colors.divider}` }}
        >
          <Button
            onClick={() => setForgotOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              color: "#fff",
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              "&:hover": {
                boxShadow: "0 8px 22px rgba(79, 70, 229, 0.45)",
              },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;
